import { useCallback, useEffect, useMemo, useState } from 'react'
import { Building2, Loader2, TrendingUp } from 'lucide-react'

import { SaleHistoryPanel } from '@/components/reports/SaleHistoryPanel'
import { StaffCommissionPanel } from '@/components/reports/StaffCommissionPanel'
import { RefundsPanel } from '@/components/reports/RefundsPanel'
import { ReportsHubPanel } from '@/components/reports/ReportsHubPanel'
import { SalonReportFilterBar } from '@/components/reports/SalonReportFilterBar'
import { SalonDataTable } from '@/components/ui/SalonDataTable'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { fetchBranchComparisonReport, formatReportMoney } from '@/lib/api/reports'
import {
  exportSalesReportCsv,
  fetchSalesReportForPeriod,
} from '@/lib/api/salesSummary'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { PERMISSIONS, hasPermission } from '@/lib/permissions'
import { resolveReportPeriodBounds } from '@/lib/reports/filters'
import { salonReportPeriodLabel } from '@/lib/reports/salonReportFilters'
import { SALON_DESK_SALES } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

/**
 * @param {string} isoDate
 */
function formatTableDate(isoDate) {
  const date = new Date(`${isoDate}T12:00:00`)
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

/**
 * @param {{ label: string, value: string }} props
 */
function SalesStat({ label, value }) {
  return (
    <div className="salon-stat-card">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-foreground">{value}</p>
    </div>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SalesRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  )
}

export function SalesPage() {
  const { user } = useAuth()
  const { activeBranchId, branches, activeBranch } = useBranch()
  const isAdmin = hasPermission(user, PERMISSIONS.ADMIN_FULL)
  const canRefund = isAdmin || hasPermission(user, PERMISSIONS.POS_VOID)
  const [activeTab, setActiveTab] = useState('summary')
  const showAllLocations =
    SALON_DESK_SALES.showAllLocationsTab && (branches?.length ?? 0) > 1
  const [scope, setScope] = useState('branch')
  const [period, setPeriod] = useState('today')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  /** @type {[Awaited<ReturnType<typeof fetchSalesReportForPeriod>> | null, Function]} */
  const [report, setReport] = useState(null)
  /** @type {[Awaited<ReturnType<typeof fetchBranchComparisonReport>> | null, Function]} */
  const [allLocationsReport, setAllLocationsReport] = useState(null)

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setAllLocationsReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    const query = {
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
    }

    try {
      if (scope === 'all' && showAllLocations) {
        const data = await fetchBranchComparisonReport(query)
        setAllLocationsReport(data)
        setReport(null)
      } else {
        const data = await fetchSalesReportForPeriod({ period, customFrom, customTo })
        setReport(data)
        setAllLocationsReport(null)
      }
    } catch (err) {
      setReport(null)
      setAllLocationsReport(null)
      setError(
        apiErrorMessage(
          err,
          scope === 'all'
            ? 'Could not load all-locations report. Try again in a moment.'
            : 'Could not load sales report. Try again in a moment.',
        ),
      )
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, bounds, scope, showAllLocations])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  async function handleExport() {
    if (scope === 'all') {
      return
    }

    setExporting(true)
    setError('')
    try {
      await exportSalesReportCsv({ period, customFrom, customTo })
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not export sales report.'))
    } finally {
      setExporting(false)
    }
  }

  const sales = report?.sales
  const currency = sales?.branch_currency ?? 'AED'
  const summary = sales?.summary ?? {}
  const dayRows = sales?.by_day ?? []
  const payments = report?.paymentMix?.by_method ?? []
  const periodLabel = salonReportPeriodLabel(period, bounds ?? {})
  const allBranchRows = allLocationsReport?.branches ?? []
  const hqCurrency = allLocationsReport?.hq_currency ?? 'AED'

  return (
    <div className="salon-page">
      <header className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-primary">
          <TrendingUp className="h-5 w-5" aria-hidden />
          <span className="text-xs font-bold uppercase tracking-widest">Reports</span>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">Sales</h1>
        <p className="mt-2 text-muted-foreground">
          {activeTab === 'history'
            ? 'Find past tickets, filter by guest or date, and reprint receipts.'
            : activeTab === 'commissions'
              ? 'Staff commission totals from completed checkouts.'
              : activeTab === 'refunds'
                ? 'Refund a completed ticket with manager approval.'
                : activeTab === 'more'
                  ? 'More salon reports in plain language.'
                  : scope === 'all'
                ? 'Compare sales across every location you can access.'
                : activeBranch?.name
                  ? `Sales for ${activeBranch.name} — pick a date range and export if needed.`
                  : 'See how much you sold — pick a date range and export if needed.'}
        </p>

        {(isAdmin || canRefund) && SALON_DESK_SALES.showAdminReports ? (
          <div className="mt-4 inline-flex flex-wrap rounded-2xl border border-border/60 bg-muted/30 p-1">
            {[
              { id: 'summary', label: 'Summary' },
              ...(isAdmin ? [
                { id: 'history', label: 'Ticket history' },
                { id: 'commissions', label: 'Staff commission' },
                { id: 'more', label: 'More reports' },
              ] : []),
              ...(canRefund ? [{ id: 'refunds', label: 'Refunds' }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={cn(
                  'rounded-xl px-4 py-2 text-sm font-medium transition-colors',
                  activeTab === tab.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                )}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        {activeTab === 'summary' && showAllLocations ? (
          <div className="mt-4 inline-flex rounded-2xl border border-border/60 bg-muted/30 p-1">
            <button
              type="button"
              className={cn(
                'rounded-xl px-4 py-2 text-sm font-medium transition-colors',
                scope === 'branch' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
              onClick={() => setScope('branch')}
            >
              This location
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors',
                scope === 'all' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
              )}
              onClick={() => setScope('all')}
            >
              <Building2 className="h-4 w-4" aria-hidden />
              All locations
            </button>
          </div>
        ) : null}
      </header>

      {activeTab === 'history' && isAdmin ? <SaleHistoryPanel /> : null}
      {activeTab === 'commissions' && isAdmin ? <StaffCommissionPanel /> : null}
      {activeTab === 'refunds' && canRefund ? <RefundsPanel /> : null}
      {activeTab === 'more' && isAdmin ? <ReportsHubPanel /> : null}

      {activeTab === 'summary' ? (
        <>
      <SalonReportFilterBar
        period={period}
        customFrom={customFrom}
        customTo={customTo}
        onPeriodChange={setPeriod}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        onExportCsv={
          SALON_DESK_SALES.showCsvExport && scope === 'branch' ? handleExport : undefined
        }
        exporting={exporting}
        loading={loading}
      />

      {error ? (
        <p className="mb-6 mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span>Loading report…</span>
        </div>
      ) : scope === 'all' && allLocationsReport ? (
        <div className="mt-6 space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">{periodLabel}</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <SalesStat
                label="Locations"
                value={String(allLocationsReport.summary?.branch_count ?? 0)}
              />
              <SalesStat
                label={`Total sales (${hqCurrency})`}
                value={allLocationsReport.summary?.total_hq_gross ?? '—'}
              />
              <SalesStat
                label="Appointments"
                value={String(allLocationsReport.summary?.total_appointment_count ?? 0)}
              />
            </div>
          </section>

          {allLocationsReport.multi_currency_footnote ? (
            <p className="text-xs text-muted-foreground">{allLocationsReport.multi_currency_footnote}</p>
          ) : null}

          <section className="salon-panel">
            <h2 className="text-lg font-semibold">By location</h2>
            <div className="mt-4 overflow-x-auto rounded-2xl border border-border/70">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Location</th>
                    <th className="px-4 py-3 font-medium">Sales</th>
                    <th className="px-4 py-3 font-medium">Gross</th>
                    <th className="px-4 py-3 font-medium">Appointments</th>
                  </tr>
                </thead>
                <tbody>
                  {allBranchRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                        No sales in this period.
                      </td>
                    </tr>
                  ) : (
                    allBranchRows.map((row) => (
                      <tr key={row.branch_id} className="border-t border-border/60">
                        <td className="px-4 py-3 font-medium">{row.branch_name}</td>
                        <td className="px-4 py-3 tabular-nums">{row.sale_count}</td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.currency_code} {formatReportMoney(row.gross_total)}
                        </td>
                        <td className="px-4 py-3 tabular-nums">{row.appointment_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">{periodLabel}</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <SalesStat
                label="Total sales"
                value={`${currency} ${formatReportMoney(summary.gross_total)}`}
              />
              <SalesStat label="Number of sales" value={String(summary.sale_count ?? 0)} />
              <SalesStat
                label="Average ticket"
                value={`${currency} ${formatReportMoney(summary.average_ticket)}`}
              />
            </div>
          </section>

          {SALON_DESK_SALES.showPaymentBreakdown && payments.length > 0 ? (
            <section className="salon-panel">
              <h2 className="text-lg font-semibold">How customers paid</h2>
              <div className="mt-2 divide-y divide-border/60">
                {payments
                  .filter((row) => Number(row.amount ?? 0) > 0)
                  .map((row) => (
                    <SalesRow
                      key={row.payment_method_id ?? row.code}
                      label={row.name ?? row.code ?? 'Payment'}
                      value={`${currency} ${formatReportMoney(row.amount)}`}
                    />
                  ))}
              </div>
            </section>
          ) : null}

          <SalonDataTable
            title="Day by day"
            columns={[
              {
                key: 'date',
                label: 'Date',
                render: (row) => <span className="font-medium">{formatTableDate(row.date)}</span>,
              },
              {
                key: 'sale_count',
                label: 'Tickets',
                className: 'tabular-nums',
                render: (row) => row.sale_count,
              },
              {
                key: 'gross_total',
                label: `Total (${currency})`,
                className: 'text-right tabular-nums font-semibold',
                headerClassName: 'text-right',
                render: (row) => formatReportMoney(row.gross_total),
              },
            ]}
            rows={dayRows.filter((row) => Number(row.sale_count ?? 0) > 0)}
            loading={loading}
            getRowKey={(row) => row.date}
            emptyLabel="No completed sales in this period."
          />
        </div>
      )}
        </>
      ) : null}
    </div>
  )
}
